import { parseBatchQueryValue } from "@shared/inventory/batch-label";
import { CHEMICAL_ROUTE_MESSAGES } from "@shared/inventory/inventory-copy";
import { NOT_FOUND_MESSAGES } from "@shared/platform/api-copy";
import { API_PATHS, CHEMICAL_LOOKUP_QUERY_KEY } from "@shared/platform/app-metadata";

import { jsonResponse } from "./demo-responses";
import { requestUrl } from "./request-url";

type BatchRow = Record<string, unknown>;

export interface BatchLookupAnswer {
  body: unknown;
  status: number;
}

export const BATCH_LOOKUP_PATH = `${API_PATHS.chemicals}/lookup`;

const OK_STATUS = 200;
const BAD_REQUEST_STATUS = 400;
const NOT_FOUND_STATUS = 404;

function requestedBatchNumber(url: URL): string | null {
  return parseBatchQueryValue(url.searchParams.get(CHEMICAL_LOOKUP_QUERY_KEY));
}

function hasBatchNumber(batchNumber: string, row: BatchRow): boolean {
  return row.batchNumber === batchNumber;
}

function hasBatchNumberInAnyCase(batchNumber: string, row: BatchRow): boolean {
  return typeof row.batchNumber === "string" && row.batchNumber.toLowerCase() === batchNumber.toLowerCase();
}

function findBatch<Row extends BatchRow>(rows: Row[], batchNumber: string): Row | null {
  const exact = rows.find(hasBatchNumber.bind(null, batchNumber));
  if (exact) return exact;
  const variants = rows.filter(hasBatchNumberInAnyCase.bind(null, batchNumber));
  return variants.length === 1 ? variants[0] : null;
}

export function demoBatchLookup<Row extends BatchRow>(url: URL, rows: Row[]): BatchLookupAnswer {
  const batchNumber = requestedBatchNumber(url);
  if (batchNumber === null) return { body: { message: CHEMICAL_ROUTE_MESSAGES.lookupBatchRequired }, status: BAD_REQUEST_STATUS };
  const found = findBatch(rows, batchNumber);
  return found
    ? { body: found, status: OK_STATUS }
    : { body: { message: NOT_FOUND_MESSAGES.chemical }, status: NOT_FOUND_STATUS };
}

function isBatchLookup(url: URL, init?: RequestInit): boolean {
  return (init?.method ?? "GET").toUpperCase() === "GET" && url.pathname === BATCH_LOOKUP_PATH;
}

async function answerBatchLookup(transport: typeof fetch, input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = requestUrl(input);
  if (!isBatchLookup(url, init)) return transport(input, init);
  const rows = await (await transport(API_PATHS.chemicals)).json();
  const answer = demoBatchLookup(url, rows);
  return jsonResponse(answer.body, answer.status);
}

export function withBatchLookup(transport: typeof fetch): typeof fetch {
  return answerBatchLookup.bind(null, transport) as typeof fetch;
}
